import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultMessagesResponse,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import Heading from '@atlaskit/heading/heading';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import Spinner from '@atlaskit/spinner/spinner';

import { token } from '@atlaskit/tokens';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

type CustomDismissCallback = () => void;

export type PlacementRenderProps = {
	dismiss?: CustomDismissCallback;
	setContent: (node: React.ReactNode) => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

type AppProps = { onMessageCountChange?: (numberOfMessages: number) => void };

const placementConfig: PlacementBrowserConfig = {
	placementId: 'software-timeline-placement',
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => (appProps) => {
	const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>();
	const [content, setContent] = useState<React.ReactNode>(null);

	const apiResponse = usePlacementApi<DefaultMessagesResponse>('/');

	const { onMessageCountChange } = appProps;

	useEffect(() => {
		const { error, data, isLoading } = apiResponse;

		if (isLoading) {
			return;
		}

		if (error) {
			throw error;
		}

		setMessages(data.messages);
	}, [apiResponse]);

	useEffect(() => {
		if (typeof messages === 'undefined') {
			return;
		}

		if (!onMessageCountChange) {
			return;
		}

		onMessageCountChange(messages.length);
	}, [messages, onMessageCountChange]);

	const handleDismiss = (messageInstanceId: string): void => {
		setMessages(() => {
			if (!messages) {
				return;
			}

			const filteredMessages = messages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);

			return filteredMessages;
		});
	};

	return (
		<Stack space="space.150">
			<Heading as={'h2'} size="medium">
				<FormattedMessage
					id="post-office.software-timeline-testing.main-heading.no-translate"
					defaultMessage="Welcome to Placements v2"
					description="heading for example placement"
				/>
			</Heading>
			<Inline space="space.200">
				<>
					{!apiResponse.isLoading && (
						<Stack as="ul" space="space.200">
							{messages?.map((props) => (
								<Inline as="li" key={props.messageInstanceId}>
									<Messages.render
										setContent={setContent}
										dismiss={() => handleDismiss(props.messageInstanceId)}
										{...props}
									/>
								</Inline>
							))}
						</Stack>
					)}
				</>
				<Box
					padding="space.200"
					xcss={{
						boxShadow: token('elevation.shadow.overlay'),
						borderRadius: token('radius.large'),
						display: 'flex',
						flexGrow: 1,
						alignSelf: 'stretch',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					{content || <Spinner size={'large'} />}
				</Box>
			</Inline>
		</Stack>
	);
});

export type PlacementFrontendProps = {
	onMessageCountChange?: (numberOfMessages: number) => void;
};
